import { tracedConsole } from "@r8er/lib/logging";
import { InputPassword } from "@r8er/components/input/password";
import { InputShortText } from "@r8er/components/input/short-text";
import buttonStyles from "@r8er/components/marketing/button/button.module.css";
import { Column, Columns } from "@r8er/components/marketing/columns";
import columnClasses from "@r8er/components/marketing/columns/columns.module.css";
import { IconLogoMarkLight } from "@r8er/components/marketing/icons";
import { Layout as MarketingLayout } from "@r8er/components/marketing/layout";
import layoutClasses from "@r8er/components/marketing/layout/layout.module.css";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { api } from "@r8er/lib/api";
import { withMiddleware } from "@r8er/lib/middleware";
import { decodePasswordResetToken } from "@r8er/lib/session";
import type { InferGetServerSidePropsType } from "next";
import Head from "next/head";
import { createRef, useState } from "react";

export const getServerSideProps = withMiddleware(
  // TODO: Fix types
  // @ts-ignore
  async function (context) {
    const {
      req,
      query: { token },
    } = context;
    // Only the token travels to the browser. The account it names is resolved
    // again server-side by the mutation, so the form cannot nominate a
    // different one.
    let resetToken: string = null;

    if (typeof token === "string") {
      try {
        decodePasswordResetToken(token);
        resetToken = token;
      } catch (error) {
        // TODO: Handle invalid tokens (expired, or wrong)
        tracedConsole.error(error);
      }
    }

    return {
      props: {
        resetToken,
      },
    };
  },
  { mode: "unauthenticated" },
);

const Watermark = () => (
  <svg
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: "translate(-75%, -15%)",
      opacity: "50%",
    }}
    xmlns="http://www.w3.org/2000/svg"
    width="649.6"
    height="1051.7"
  >
    <path
      fill="#f0f0f0"
      d="M516 79a270 270 0 00-382 0 270 270 0 00-23 356l49-48c-56-79-49-190 22-260a203 203 0 01286 0c79 79 79 207 0 286L325 556l-74-73-48 47 122 122 191-191a270 270 0 000-382"
    />
    <path
      fill="#f0f0f0"
      d="M555 497l-15-14-48 47 15 15a258 258 0 010 364 258 258 0 01-364 0 258 258 0 010-364l182-182 74 74 48-48-122-122L95 497a325 325 0 000 460 325 325 0 00460 0 325 325 0 000-460"
    />
  </svg>
);

const SignIn = ({
  resetToken,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [email, setEmail] = useState("");
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [error, setError] = useState<string>(null);
  const formRef = createRef<HTMLFormElement>();

  const resetPassword = async () => {
    const res = await api.user.resetPassword.mutate({ email });

    setError("");

    if (res === true) return setEmailSuccess(true);
    if (res.error) return setError(res.error);
  };

  const updatePassword = async () => {
    if (!formRef.current)
      return setError(
        "We couldn't update your password. Please try again or contact us for support",
      );
    if (password1 !== password2) return setError("Passwords must match");

    const passwordStrengthElement =
      formRef.current?.elements?.["password-strength"];
    const passwordFeedbackWarningElement =
      formRef.current?.elements?.["password-feedback-warning"];
    const passwordFeedbackSuggestionsElement =
      formRef.current?.elements?.["password-feedback-suggestions"];
    if (passwordStrengthElement) {
      const passwordStrength = parseInt(passwordStrengthElement.value);

      if (passwordStrength < 2) {
        return setError(
          `Password too weak: ${
            passwordFeedbackWarningElement?.value ||
            passwordFeedbackSuggestionsElement?.value ||
            "Password too short. Please enter at least 4 characters"
          }`,
        );
      }
    }

    setError("");

    const res = await api.user.updatePassword.mutate({
      token: resetToken,
      password: password1,
    });

    if (res) return setUpdateSuccess(true);
  };

  return (
    <MarketingLayout>
      <Head>
        <title>Reset Password - R8ER</title>
      </Head>

      <Columns
        count={2}
        style={{
          minHeight: "950px",
          gridColumnGap: 0,
          borderBottom: "8px solid var(--brand-secondary)",
        }}
      >
        <Column classList={[columnClasses.marketing__columns_inner_background]}>
          <div className={columnClasses.marketing__columns_inner_center_wide}>
            <Watermark />

            <h2 style={{ color: "var(--brand-secondary)" }}>
              Reset your password
            </h2>

            {error && <span className={layoutClasses.text__red}>{error}</span>}

            {resetToken ? (
              updateSuccess ? (
                <div>
                  Password updated. Return to the login page to login to your
                  account.
                </div>
              ) : (
                <form
                  ref={formRef}
                  method="post"
                  style={{ marginTop: "2.5rem" }}
                >
                  <InputPassword
                    id="password-1"
                    name="password-1"
                    showStrength
                    onChange={(e) =>
                      setPassword1(e.newValue as unknown as string)
                    }
                    placeholder="Password"
                  />
                  <InputPassword
                    id="password-2"
                    name="password-2"
                    onChange={(e) =>
                      setPassword2(e.newValue as unknown as string)
                    }
                    placeholder="Confirm Password"
                  />
                  <div>
                    <button
                      onClick={updatePassword}
                      className={buttonStyles.button}
                      type="button"
                    >
                      Change Password
                    </button>
                  </div>
                </form>
              )
            ) : emailSuccess ? (
              <div>An email has been sent to your inbox.</div>
            ) : (
              <form method="post" style={{ marginTop: "2.5rem" }}>
                <InputShortText
                  id="email"
                  name="email"
                  onChange={(e) => setEmail(e.newValue as unknown as string)}
                  placeholder="Email"
                  inputMode="email"
                />
                <div>
                  <button
                    onClick={resetPassword}
                    className={buttonStyles.button}
                    type="button"
                  >
                    Reset Password
                  </button>
                </div>
              </form>
            )}
          </div>
        </Column>
        <Column
          classList={[columnClasses.marketing__columns_inner_overlay]}
          style={{
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundImage: "url(/signin-cover.png)",
          }}
        >
          <div
            className={columnClasses.marketing__columns_inner_center_wide}
            style={{ maxWidth: "550px" }}
          >
            <h2>Welcome to R8ER</h2>
            <p>
              The world's first, professionally vetted, digital evaluation
              platform for disruptive healthcare that connects technology with
              investment.
            </p>
            <div style={{ marginTop: "4rem" }}>
              <IconLogoMarkLight />
            </div>
          </div>
        </Column>
      </Columns>

      <Newsletter />
    </MarketingLayout>
  );
};

export default SignIn;
